import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { useAuth } from "./AuthContext";
import { apiClient } from "../apiClient";
import { normalizeSelectOptions } from "../utils/normalizeSelectOptions";


type SettingsContextType = {
  associati: any[];
  servizi: any[];
  esiti: any[];
  loading: boolean;
  addItem: (type: "associati" | "servizi" | "esiti", value: string) => Promise<void>;
  deleteItem: (type: "associati" | "servizi" | "esiti", value: string) => Promise<void>;
  refreshData: () => Promise<void>;
  deleteDatabaseData: (tipo: string, password?: string) => Promise<{ success: boolean; message: string }>;
};

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export const SettingsProvider = ({ children }: { children: ReactNode }) => {
  const { isAuthenticated } = useAuth();
  const [associati, setAssociati] = useState<any[]>([]);
  const [servizi, setServizi] = useState<any[]>([]);
  const [esiti, setEsiti] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const refreshData = async () => {
    if (!isAuthenticated) return;
    setLoading(true);
    try {
      const [assocRes, servRes, esitiRes] = await Promise.all([
        apiClient.get("json/associati"),
        apiClient.get("json/servizi"),
        apiClient.get("json/esiti"),
      ]);
      setAssociati(normalizeSelectOptions(assocRes.data));
      setServizi(normalizeSelectOptions(servRes.data));
      setEsiti(normalizeSelectOptions(esitiRes.data));
    } catch (err) {
      console.error("Errore fetch settings:", err);
      setAssociati([]);
      setServizi([]);
      setEsiti([]);
    } finally {
      setLoading(false);
    }
  };

  const addItem = async (type: "associati" | "servizi" | "esiti", value: string) => {
    if (!value.trim() || !isAuthenticated) return;
    const newItem = { label: value.trim(), value: value.trim() };
    try {
      await apiClient.post(`json/${type}`, newItem);
      switch (type) {
        case "associati":
          setAssociati(prev => [newItem, ...prev]);
          break;
        case "servizi":
          setServizi(prev => [newItem, ...prev]);
          break;
        case "esiti":
          setEsiti(prev => [newItem, ...prev]);
          break;
      }
    } catch (err) {
      console.error("Errore aggiunta:", err);
      throw err;
    }
  };

  const deleteItem = async (type: "associati" | "servizi" | "esiti", value: string) => {
    if (!isAuthenticated) return;
    try {
      await apiClient.delete(`json/${type}/${encodeURIComponent(value)}`);
      switch (type) {
        case "associati":
          setAssociati(prev => prev.filter(item => item.value !== value));
          break;
        case "servizi":
          setServizi(prev => prev.filter(item => item.value !== value));
          break;
        case "esiti":
          setEsiti(prev => prev.filter(item => item.value !== value));
          break;
      }
    } catch (err) {
      console.error("Errore eliminazione:", err);
      throw err;
    }
  };

  const deleteDatabaseData = async (tipo: string, password?: string) => {
    if (!isAuthenticated) return { success: false, message: "Non autenticato" };
    try {
      const res = await apiClient.delete(`elimina/${tipo}`, {
        data: tipo === "tutto" ? { password } : {},
      });
      return { success: true, message: res.data.message || "Eliminazione completata" };
    } catch (err: any) {
      console.error("Errore eliminazione database:", err);
      return {
        success: false,
        message: err.response?.data?.detail || "Errore durante l'eliminazione",
      };
    }
  };

  useEffect(() => {
    refreshData();
  }, [isAuthenticated]);

  return (
    <SettingsContext.Provider
      value={{ associati, servizi, esiti, loading, addItem, deleteItem, refreshData, deleteDatabaseData }}
    >
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) throw new Error("useSettings deve essere usato dentro <SettingsProvider>");
  return context;
};
